/**
 * Provider de autenticación: hidrata la sesión y la expone vía `AuthContext`.
 *
 * Hidrata desde `GET /api/v1/auth/me` con TanStack Query (la red la gobierna
 * `lib/api`: cookie httpOnly + refresh en 401). El resultado se proyecta al
 * contexto como **estado de UI** (user + permisos + sede activa), nunca a
 * `localStorage` y nunca con el token (`docs/frontend_architecture.md`, Pr. 3-4).
 *
 * Integra el store `lib/sede` existente: cuando el backend resuelve la sede
 * activa, se refleja en el store para que `lib/api` la reenvíe como `X-Sede-Id`
 * en las siguientes peticiones (incluso tras recargar la página). El selector de
 * sede (`features/auth`) llama a `setActiveSede`, que fija el store y revalida la
 * identidad para recalcular `active_sede` con la nueva sede.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";

import { fetchMe, logout as logoutRequest } from "@/features/auth/api";
import { getActiveSedeId, setActiveSedeId } from "@/lib/sede";

import { AuthContext, type AuthStatus, type AuthContextValue } from "./AuthContext";
import { userHasPermission, userIsMultiSede } from "./permissions";
import { AUTH_ME_QUERY_KEY } from "./queryKeys";

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // Fuente de verdad de la sesión. `retry: false` evita reintentar un 401 (no
  // autenticado): ese error es la señal de "no hay sesión", no un fallo de red.
  const meQuery = useQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: fetchMe,
    retry: false,
    staleTime: Infinity,
  });

  const { data, isPending, isError, refetch } = meQuery;
  const user = data?.user ?? null;
  const activeSede = data?.active_sede ?? null;
  // Mismo criterio que el backend (rol `admin` o permiso `all`): determina si el
  // usuario ve el selector y, abajo, si se auto-selecciona su sede de casa.
  const isMultiSede = user !== null && userIsMultiSede(user);
  // Flags de módulos por sede (espejo del SSR). Opcionales en el contrato: si el
  // backend aún no los emite, se asumen desactivados (no se muestra la entrada).
  const sedeInvActivo = data?.sede_inv_activo ?? false;
  const sedeLaserActivo = data?.sede_laser_activo ?? false;

  // Refleja la sede resuelta por el backend en el store que lee `lib/api`. Así,
  // tras hidratar (o tras un reload), las requests llevan el `X-Sede-Id` correcto
  // sin que el usuario tenga que reelegir. Solo cuando el backend la resolvió.
  useEffect(() => {
    if (activeSede !== null && getActiveSedeId() !== activeSede.id) {
      setActiveSedeId(activeSede.id);
    }
  }, [activeSede]);

  const status: AuthStatus = useMemo(() => {
    // El error manda sobre el `data` rancio: tras un logout, `/auth/me` responde
    // 401 pero TanStack Query conserva el último `data` (no lo limpia en error).
    // Un 401 significa "sin sesión", así que se evalúa primero (la guarda
    // redirige a login). Con `data` y sin error: autenticado. Si no, cargando.
    if (isError) {
      return "unauthenticated";
    }
    if (data) {
      return "authenticated";
    }
    return isPending ? "loading" : "unauthenticated";
  }, [data, isError, isPending]);

  const setActiveSede = useCallback(
    (sedeId: number) => {
      setActiveSedeId(sedeId);
      // Revalida la identidad: `/auth/me` recalcula `active_sede` con el nuevo
      // header `X-Sede-Id` que `lib/api` inyecta desde el store ya actualizado.
      void refetch();
    },
    [refetch],
  );

  // Guarda anti-bucle del auto-seleccionado: recuerda la sede que ya intentamos
  // fijar en esta sesión. Si el backend rechazara `user.sede_id` (no debería: es
  // su sede de casa), `activeSede` seguiría null y sin esta guarda el efecto se
  // re-dispararía en cada render → bucle. Se reinicia al cerrar sesión (sin
  // usuario) para que un nuevo login vuelva a armar el auto-seleccionado.
  const autoSelectedSedeIdRef = useRef<number | null>(null);

  // Sede por defecto del multi-sede/admin: su SEDE ASIGNADA (`user.sede_id`). El
  // mono-sede ya arranca en su sede (el backend la resuelve solo); el hueco está
  // en el multi-sede, que en el primer `/auth/me` no manda `X-Sede-Id` y recibe
  // `active_sede: null` → el selector queda vacío y los dominios dan 400. Aquí,
  // al detectar ese estado, fijamos su sede de casa para que arranque SIEMPRE en
  // ella (sin persistencia: el usuario la quiere por defecto en cada sesión) y
  // pueda cambiarla luego con el selector. Sin `user` (logout) no actúa.
  useEffect(() => {
    if (!isMultiSede || user === null || activeSede !== null) {
      // Estado resuelto (o no aplica): rearma la guarda para el próximo arranque
      // que vuelva a quedar sin sede (p. ej. tras logout, cuando `user` es null).
      if (user === null) {
        autoSelectedSedeIdRef.current = null;
      }
      return;
    }
    if (autoSelectedSedeIdRef.current === user.sede_id) {
      // Ya lo intentamos para esta sede en esta sesión: no reintentar (anti-bucle).
      return;
    }
    autoSelectedSedeIdRef.current = user.sede_id;
    setActiveSede(user.sede_id);
  }, [isMultiSede, user, activeSede, setActiveSede]);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      // Limpia el estado del cliente pase lo que pase en el backend: sin sede
      // activa y sin ningún dato cacheado de otras pantallas. Se elimina todo el
      // cache MENOS `/auth/me`, que se vuelve a pedir explícitamente: con la
      // cookie ya invalidada responderá 401 y el contexto pasará a
      // "no autenticado" (la guarda redirige a login). Refetch explícito porque
      // limpiar el cache no garantiza por sí solo re-pedir la query observada.
      setActiveSedeId(null);
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== AUTH_ME_QUERY_KEY[0],
      });
      await queryClient.refetchQueries({ queryKey: AUTH_ME_QUERY_KEY });
    }
  }, [queryClient]);

  const value: AuthContextValue = useMemo(
    () => ({
      status,
      user,
      permissions: user?.permissions ?? [],
      activeSede,
      isMultiSede,
      sedeInvActivo,
      sedeLaserActivo,
      hasPermission: (permission: string) =>
        user !== null && userHasPermission(user, permission),
      setActiveSede,
      logout,
    }),
    [
      status,
      user,
      activeSede,
      isMultiSede,
      sedeInvActivo,
      sedeLaserActivo,
      setActiveSede,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
